import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CollapsibleTileContainer } from '@sorare/core/src/atoms/layout/CollapsibleTileContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Box, LabelPosition } from '@sorare/core/src/atoms/ui/Box';
import { isTypeFn } from '@sorare/core/src/gql';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { ClaimDailyPackDialog } from '../ClaimDailyPackDialog';
import { Mission } from '../Mission';
import { MissionTile } from '../MissionTile';
import { MissionTimeLeft } from '../MissionTimeLeft';
import type { CommonGameMissions_currentUser } from './__generated__/index.graphql';

const Wrapper = styled(Box)`
  position: relative;
  overflow: hidden;
  border: 2px solid #01fff8;
  padding: 0;
  background: linear-gradient(120deg, #01534e -6.77%, #202020 44.68%);
`;

const WithTilesBackground = styled(Vertical)`
  --tile-size: 44px;
  --offset: calc(var(--tile-size) / 2);
  background: linear-gradient(to bottom, transparent, var(--c-black) 80%),
    repeating-linear-gradient(
      to bottom,
      #01fff8 var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    repeating-linear-gradient(
      to right,
      #01fff8 var(--offset),
      transparent calc(var(--offset) + 1px),
      transparent calc(var(--offset) + var(--tile-size))
    ),
    linear-gradient(to bottom, #3ddc8435, #3ddc8400);
  padding: calc(5 * var(--unit)) var(--double-unit) var(--double-unit);
`;
type Props = {
  currentUser: Nullable<CommonGameMissions_currentUser>;
};
const CommonGameMissionsList = ({
  currentUser,
  onClaim,
}: Props & {
  onClaim: (missionId: string) => void;
}) => {
  const missions =
    currentUser?.managerProgression?.categories?.[0]?.categoryTasks.filter(
      isTypeFn('ManagerProgressionMission')
    );

  const isLaptopAndAbove = useIsLaptop();
  if (!missions?.length) {
    return null;
  }

  if (isLaptopAndAbove) {
    return (
      <Wrapper
        labelProps={{
          color: 'var(--c-black)',
          bgColor: '#01FFF8',
          position: LabelPosition.LEFT,
          value: (
            <LabelM uppercase bold>
              <FormattedMessage
                id="ArenaPage.CommonGameMissions"
                defaultMessage="Missions"
              />
            </LabelM>
          ),
        }}
      >
        <WithTilesBackground gap={2}>
          {!!missions.length && (
            <Vertical gap={1.5}>
              <SBHorizontal>
                <LabelL color="var(--c-rivals-friendlies)" bold>
                  <FormattedMessage
                    id="CommonGameMissions.daily.title"
                    defaultMessage="Daily"
                  />
                </LabelL>
                <MissionTimeLeft mission={missions[0]} />
              </SBHorizontal>
              <Vertical>
                {missions.map(mission => (
                  <Mission
                    key={mission.id}
                    mission={mission}
                    onClaim={() => onClaim(mission.id)}
                  />
                ))}
              </Vertical>
            </Vertical>
          )}
        </WithTilesBackground>
      </Wrapper>
    );
  }
  return (
    <CollapsibleTileContainer>
      {missions.map(mission => (
        <MissionTile
          key={mission.id}
          mission={mission}
          onClaim={() => onClaim(mission.id)}
        />
      ))}
    </CollapsibleTileContainer>
  );
};

export const CommonGameMissions = ({ currentUser }: Props) => {
  const [packDialogMissionId, setPackDialogMissionId] = useState<string>();

  const missions =
    currentUser?.managerProgression?.categories?.[0]?.categoryTasks.filter(
      isTypeFn('ManagerProgressionMission')
    );
  const claimingMission = missions?.find(
    mission => mission.id === packDialogMissionId
  );
  return (
    <>
      <CommonGameMissionsList
        currentUser={currentUser}
        onClaim={missionId => {
          setPackDialogMissionId(missionId);
        }}
      />
      <ClaimDailyPackDialog
        mission={claimingMission}
        open={!!claimingMission}
        onClose={() => setPackDialogMissionId(undefined)}
      />
    </>
  );
};

CommonGameMissions.fragments = {
  currentUser: gql`
    fragment CommonGameMissions_currentUser on CurrentUser {
      slug
      managerProgression(sport: $sport) {
        id
        categories(names: COMMON_GAME) {
          id
          name
          categoryTasks {
            ... on ManagerProgressionMission {
              id
              claimableAt
              ...Mission_managerProgressionMission
              ...MissionTile_managerProgressionMission
              ...MissionTimeLeft_managerProgressionMission
              ...ClaimDailyPackDialog_managerProgressionMission
            }
            ... on ManagerProgressionTask {
              id
            }
          }
        }
      }
    }
    ${Mission.fragments.managerProgressionMission}
    ${MissionTile.fragments.managerProgressionMission}
    ${MissionTimeLeft.fragments.managerProgressionMission}
    ${ClaimDailyPackDialog.fragments.managerProgressionMission}
  ` as TypedDocumentNode<CommonGameMissions_currentUser>,
};
